import { useCallback, useContext } from 'react';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { getFormattedColumnValue } from '../ObjectTable/ObjectTableRow';
import EDView from '../../../../../../BaseComponents/View/EDView';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { makeStyles } from 'tss-react/mui';
import EDBreadcrumb from '../../../../../../BaseComponents/Breadcrumb/EDBreadcrumb';
import { ThemeProvider, StyledEngineProvider } from '@mui/material';
import { createPortal } from 'react-dom';
import {
  NewId,
  selectedId,
} from '../../../../../../Components/MasterDetail/MasterDetailUtils';
import {
  findRoutineDetaljVyAssociationName,
  findRoutineDetaljVyCollectionName,
} from '../../../Const/Rutiner';
import { AvenyThemeDark } from '../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import EDKeybind from '../../../../../../BaseComponents/Keybinds/EDKeybind';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import defaultColumns from '../../../Utils/defaultColumns';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import { useIsBreakpoint } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { findBreadcrumbsLabel } from '../../../Const/BusinessConfigs';
import { useLocation } from 'react-router';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import {
  HEADER,
  HeaderReference,
  SEPARATOR,
} from '../../RutinBreadcrumbs/BreadcrumbsContext';
import { EDBreadcrumbSeparator } from '../../../../../../BaseComponents/Breadcrumbs/EDBreadcrumbSeparator';

interface IProps {
  caption?: string;
  columns: readonly IColumnDef[];
  data: ICollectionDataType[] | null;
  selectedId: TSelectedDetail;
  currentIndex: number | null;
  selectDetail: (id: TSelectedDetail, force?: boolean) => void;
  collection: IClientBusinessModel;
  next?: () => void;
  prev?: () => void;
  currentlyOpen?: boolean;
  headerRef: HeaderReference;
  stepperRef: HTMLDivElement | null;
  rutinKod: string;
  disableBreadcrumbLink?: boolean;
}

interface IViewProps {
  itemCaption: string;
  indexCaption: string;
  selectItem?: () => void;
  detaljVyCollectionName: string | undefined;
  next?: () => void;
  prev?: () => void;
  currentlyOpen?: boolean;
  headerRef: HeaderReference;
  stepperRef: HTMLDivElement | null;
  disableBreadcrumbLink?: boolean;
}
const useStyles = makeStyles()(
  () =>
    ({
      fullWidth: {
        width: '100%',
        overflow: 'hidden',
        textAlign: 'left',
        '& button': {
          textAlign: 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          // maxWidth: 'calc(100% - 48px)',
        },
      },
      flexContainer: {
        display: 'flex',
      },
      clickable: {
        cursor: 'pointer',
      },
      selectorPadding: {
        paddingRight: 8,
      },
    } as const)
);

export const BusinessDataDetailHeader = (props: IProps) => {
  const { data: _data, ...other } = props;

  const { avenyCollectionMap } = useContext(ModelContext);
  const location = useLocation();

  let columns = props.columns;
  let collection = props.collection;
  const detaljVyCollectionName = findRoutineDetaljVyCollectionName(
    props.rutinKod
  );
  const detaljVyAssociationName = findRoutineDetaljVyAssociationName(
    props.rutinKod
  );

  if (detaljVyCollectionName) {
    columns = defaultColumns(detaljVyCollectionName);
    const detaljVyCollection = findCollectionStatic(detaljVyCollectionName);
    if (detaljVyCollection) {
      collection = detaljVyCollection;
    }
  }

  let items = props.data;
  if (detaljVyAssociationName && props.data) {
    items = props.data.map(
      item => item._Associations?.[detaljVyAssociationName]
    );
  }

  const item = items
    ? items.find(item => item?.id === selectedId(props.selectedId))
    : null;

  const breadcrumbsLabel = findBreadcrumbsLabel(
    props.collection.CollectionName
  );

  const itemCaption = item
    ? ((props.caption && props.caption + ': ') || '') +
      columns
        .filter(col => col.header)
        .map(col => {
          return getFormattedColumnValue(
            avenyCollectionMap,
            item,
            col,
            props.collection
          );
        })
        .join(' ')
    : props.selectedId === NewId
    ? `Skapa ny ${
        breadcrumbsLabel
          ? breadcrumbsLabel(location)
          : collection.RootType.SOLabel.toLowerCase()
      }`
    : collection.RootType.SOLabel;

  const currentIndex =
    props.currentIndex !== null ? props.currentIndex + 1 : '';
  const maxIndex = props.data ? props.data.length : '';
  const indexCaption = item
    ? `${
        props.caption || collection.RootType.SOLabel
      } ${currentIndex} av ${maxIndex}`
    : '';

  const selectItem = !props.currentlyOpen
    ? () => props.selectDetail(props.selectedId)
    : undefined;

  return (
    <BusinessDataDetailHeaderView
      itemCaption={itemCaption}
      indexCaption={indexCaption}
      selectItem={selectItem}
      detaljVyCollectionName={detaljVyCollectionName}
      disableBreadcrumbLink={props.disableBreadcrumbLink}
      {...other}
    />
  );
};

export const BusinessDataDetailHeaderView = (props: IViewProps) => {
  const { classes } = useStyles();
  const { prev, next } = props;
  const isXs = useIsBreakpoint('xs');

  const handlePrev = useCallback(() => {
    prev?.();
  }, [prev]);

  const handleNext = useCallback(() => {
    next?.();
  }, [next]);

  const hideArrows = Boolean(props.detaljVyCollectionName);
  return (
    <>
      {props.headerRef?.[SEPARATOR].current &&
        !props.disableBreadcrumbLink &&
        createPortal(
          <EDBreadcrumbSeparator />,
          props.headerRef[SEPARATOR].current
        )}
      {props.headerRef?.[HEADER].current &&
        !props.disableBreadcrumbLink &&
        createPortal(
          <div className={classes.fullWidth}>
            <EDView>
              <div className={classes.flexContainer}>
                <EDBreadcrumb
                  onClick={props.selectItem}
                  caption={props.itemCaption}
                />
              </div>
            </EDView>
          </div>,
          props.headerRef[HEADER].current
        )}
      {props.currentlyOpen &&
        props.stepperRef &&
        !hideArrows &&
        createPortal(
          <EDTypography
            color="inherit"
            className={classes.selectorPadding}
            noWrap
          >
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={AvenyThemeDark}>
                <EDKeybind
                  keymap="ArrowLeft"
                  ctrl
                  global
                  onKeyDown={handlePrev}
                >
                  <EDIconButton
                    tooltip={`Föregående (ctrl + vänsterpil)`}
                    icon="chevron_left"
                    onClick={handlePrev}
                    disabled={!props.prev}
                    iconColor="inherit"
                    padding={isXs ? 'normal' : 'dense'}
                    margin={isXs ? 'none' : undefined}
                    testId="previousIconButton"
                  />
                </EDKeybind>
                {props.indexCaption}
                <EDKeybind
                  keymap="ArrowRight"
                  ctrl
                  global
                  onKeyDown={handleNext}
                >
                  <EDIconButton
                    tooltip={`Nästa (ctrl + högerpil)`}
                    icon="chevron_right"
                    onClick={handleNext}
                    disabled={!props.next}
                    iconColor="inherit"
                    padding={isXs ? 'normal' : 'dense'}
                    margin={isXs ? 'none' : undefined}
                    testId="nextIconButton"
                  />
                </EDKeybind>
              </ThemeProvider>
            </StyledEngineProvider>
          </EDTypography>,
          props.stepperRef
        )}
    </>
  );
};
export default BusinessDataDetailHeader;
